<template>
  <div class="admin-page admin-swagger">
    <el-card shadow="never">
      <p class="desc">
        在线浏览 API 文档（Swagger 2.0）。数据来自 <code>/api/v1/doc/swagger</code>，仅 admin / superadmin 可访问；「Try it out」会自动附带当前登录 Token。
      </p>

      <el-alert
        v-if="!isAdmin"
        type="warning"
        title="当前账号无权限"
        description="需要管理员（admin 或 superadmin）角色。"
        show-icon
        :closable="false"
      />

      <template v-else>
        <div class="toolbar">
          <el-button type="primary" :loading="loading" @click="loadSpec">刷新文档</el-button>
          <el-button :disabled="!specRaw" @click="downloadSpec">下载 JSON</el-button>
        </div>
        <div v-if="error" class="error-box">{{ error }}</div>
        <div id="swagger-ui-container" ref="swaggerContainer" class="swagger-host" />
      </template>
    </el-card>
  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import axios from 'axios'
import { ElMessage } from 'element-plus'
import { isSystemAdmin } from '@/utils/sysRole'

const SWAGGER_UI_VERSION = '5.11.0'
const SWAGGER_UI_BASE = `https://unpkg.com/swagger-ui-dist@${SWAGGER_UI_VERSION}`

let swaggerAssetsPromise = null

function loadSwaggerUiAssets() {
  if (swaggerAssetsPromise) {
    return swaggerAssetsPromise
  }
  swaggerAssetsPromise = new Promise((resolve, reject) => {
    if (!document.getElementById('swagger-ui-css')) {
      const link = document.createElement('link')
      link.id = 'swagger-ui-css'
      link.rel = 'stylesheet'
      link.href = `${SWAGGER_UI_BASE}/swagger-ui.css`
      document.head.appendChild(link)
    }
    if (window.SwaggerUIBundle) {
      resolve(window.SwaggerUIBundle)
      return
    }
    const script = document.createElement('script')
    script.src = `${SWAGGER_UI_BASE}/swagger-ui-bundle.js`
    script.onload = () => {
      if (window.SwaggerUIBundle) {
        resolve(window.SwaggerUIBundle)
      } else {
        reject(new Error('Swagger UI 脚本加载失败'))
      }
    }
    script.onerror = () => reject(new Error('Swagger UI 脚本加载失败'))
    document.body.appendChild(script)
  })
  return swaggerAssetsPromise
}

export default {
  name: 'AdminSwaggerView',
  setup() {
    const loading = ref(false)
    const error = ref('')
    const specRaw = ref('')
    const swaggerContainer = ref(null)
    let swaggerUi = null

    const isAdmin = computed(() => {
      try {
        const user = JSON.parse(localStorage.getItem('user_info') || '{}')
        return isSystemAdmin(user.sysRole)
      } catch {
        return false
      }
    })

    const destroySwaggerUi = () => {
      if (swaggerUi?.getSystem) {
        try {
          swaggerUi.getSystem().store?.dispatch?.({ type: 'spec/updateUrl', payload: '' })
        } catch {
          // ignore
        }
      }
      swaggerUi = null
      if (swaggerContainer.value) {
        swaggerContainer.value.innerHTML = ''
      }
    }

    const loadSpec = async () => {
      if (!isAdmin.value) {
        ElMessage.warning('需要管理员权限')
        return
      }

      loading.value = true
      error.value = ''
      destroySwaggerUi()

      try {
        const token = localStorage.getItem('jwt_token')
        const response = await axios.get('/api/v1/doc/swagger', {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'text',
          timeout: 120000
        })

        const text = typeof response.data === 'string' ? response.data : JSON.stringify(response.data)
        if (!text || !text.trim()) {
          throw new Error('Swagger 文档为空')
        }

        let spec
        try {
          spec = JSON.parse(text)
        } catch {
          throw new Error('Swagger 文档不是合法 JSON')
        }

        specRaw.value = text
        const SwaggerUIBundle = await loadSwaggerUiAssets()

        swaggerUi = SwaggerUIBundle({
          spec,
          dom_id: '#swagger-ui-container',
          deepLinking: true,
          docExpansion: 'none',
          defaultModelsExpandDepth: 1,
          persistAuthorization: true,
          requestInterceptor: (req) => {
            if (token) {
              req.headers.Authorization = `Bearer ${token}`
            }
            return req
          }
        })
      } catch (err) {
        console.error('load swagger failed:', err)
        if (err.response?.data) {
          try {
            const body = typeof err.response.data === 'string'
              ? JSON.parse(err.response.data)
              : err.response.data
            error.value = body.message || '加载失败'
          } catch {
            error.value = err.response?.status === 401
              ? '未登录或登录已过期'
              : err.response?.status === 403
                ? '仅管理员可访问 Swagger 文档'
                : (err.message || '加载失败')
          }
        } else {
          error.value = err.message || '加载失败'
        }
        ElMessage.error(error.value)
      } finally {
        loading.value = false
      }
    }

    const downloadSpec = () => {
      if (!specRaw.value) return
      const blob = new Blob([specRaw.value], { type: 'application/json;charset=utf-8' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `coolpeople-api-swagger-${Date.now()}.json`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    }

    onMounted(() => {
      if (isAdmin.value) {
        loadSpec()
      } else {
        ElMessage.warning('需要管理员权限访问此页面')
      }
    })

    onBeforeUnmount(() => {
      destroySwaggerUi()
    })

    return {
      loading,
      error,
      specRaw,
      isAdmin,
      swaggerContainer,
      loadSpec,
      downloadSpec
    }
  }
}
</script>

<style scoped>
.desc {
  color: var(--el-text-color-regular);
  line-height: 1.6;
  margin: 0 0 16px;
}

.toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
}

.error-box {
  margin-bottom: 12px;
  padding: 12px;
  border-radius: 6px;
  background: var(--el-color-danger-light-9);
  color: var(--el-color-danger);
}

.swagger-host {
  min-height: 480px;
}

:deep(.swagger-ui) {
  font-family: inherit;
}

:deep(.swagger-ui .topbar) {
  display: none;
}
</style>
